import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Sync a tab (or similar enum) to a URL search param so selection survives refresh.
 * Invalid / missing values fall back to `fallback`. Updates use `replace: true`.
 */
export function useQueryTab<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value = allowed.includes(raw as T) ? (raw as T) : fallback;

  const setValue = useCallback(
    (next: T) => {
      const nextParams = new URLSearchParams(params);
      if (next === fallback) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, next);
      }
      setParams(nextParams, { replace: true });
    },
    [fallback, key, params, setParams],
  );

  return [value, setValue];
}
